import { memo, useState, type PropsWithChildren } from "react";
import LoadingButton, { type LoadingButtonProps } from "@mui/lab/LoadingButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import type { GridSelectionModel } from "@mui/x-data-grid";
import { useRefetchFiltersResource, useSimpleRequest } from "hooks";
import type { Country } from "features/general/types";
import { withSx } from "ui/atoms/Modal";
import { TriggerableDialog, type TriggerableDialogProps } from "ui/molecules/Dialog";
import ActionPanel from "ui/molecules/ActionPanel";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import { FormField } from "ui/forms/OpenBanking/BankingSDK";

type Props = Partial<TriggerableDialogProps> & {
    readonly selectionModel: GridSelectionModel;
    readonly onSelectionModelChange: (model: GridSelectionModel) => void;
    readonly onConfirm: (iso2Countries: Array<Country['iso2']>) => Promise<Response>;
    readonly TriggerComponentProps?: LoadingButtonProps;
    readonly ConfirmButtonProps?: LoadingButtonProps;
};

const MassUpdateOpenBankingResource = ({
    selectionModel,
    onSelectionModelChange,
    TriggerComponentProps,
    ConfirmButtonProps,
    onConfirm,
    children,
    ...restProps
}: PropsWithChildren<Props>) => {
    const [isLoading, setLoading] = useState(false);
    const [iso2Countries, setIso2Countries] = useState<Array<Country['iso2']>>([]);

    const refetch = useRefetchFiltersResource();

    const request = useSimpleRequest({
        setLoading,
        shouldNotifyOnSuccess: true
    });

    const handleConfirm = () => {
        setLoading(true);

        return request(() => onConfirm(iso2Countries), {
            onSuccess: () => {
                setIso2Countries([]);
                onSelectionModelChange([]);
                refetch(3000);
            }
        })
            .finally(() => setLoading(false));
    };

    return (
        <TriggerableDialog
            TriggerComponent={({ onClick }) => (
                <LoadingButton
                    color='secondary'
                    variant='outlined'
                    {...TriggerComponentProps}
                    onClick={onClick}
                    disabled={!selectionModel.length}
                    data-test-id='openBanking.table.controls.massUpdate'
                >
                    {children}
                </LoadingButton>
            )}
            DialogActionsComponent={({ onClose }) => (
                <ActionPanel
                    sx={{
                        border: 'none',
                        p: 0,
                        m: 0
                    }}
                    CancelActionProps={{
                        disabled: isLoading
                    }}
                    SaveActionProps={{
                        children: 'Update',
                        ...ConfirmButtonProps,
                        loading: isLoading
                    }}
                    onCancel={onClose}
                    onSave={() => (
                        handleConfirm()
                            .then(() => onClose())
                    )}
                />
            )}
            {...restProps}
            ModalProps={{
                disableCloseOnBackdropClick: true,
                ...restProps.ModalProps,
                DialogProps: {
                    ...withSx({
                        maxWidth: 473
                    }, {
                        actions: {
                            justifyContent: 'center'
                        },
                        content: {
                            borderBottom: 'none'
                        }
                    }),
                    ...restProps.ModalProps?.DialogProps
                }
            }}
        >
            <Box>
                <Typography
                    variant='h6'
                    gutterBottom
                >
                    Following ids will be updated:
                </Typography>
                <Typography
                    variant='body2'
                    sx={{
                        mb: 2
                    }}
                >
                    {selectionModel.join(', ')}
                </Typography>
            </Box>
            <CountryIso2
                fullWidth
                disableClearable
                layout={null}
                value={iso2Countries}
                onChange={(_, value) => setIso2Countries(value)}
                data-test-id='openBanking.table.filters.item.countryISO'
                TextFieldProps={{
                    name: FormField.CountryIso,
                    label: 'Country'
                }}
            />
        </TriggerableDialog>
    );
};

export default memo(MassUpdateOpenBankingResource);
